/*
 * ohp-interactions.js — shared OptiHealth interaction behaviors
 * ------------------------------------------------------------------
 * Vanilla JS, no framework, event-delegated at the document level so it
 * works with sections inserted dynamically (theme editor, Ajax).
 *
 * What this file handles:
 *   1. FAQ accordion — progressive smooth open/close for the native
 *      <details> markup from snippets/ohp-faq-item.liquid.
 *   2. Mobile menu / generic drawer — slide-in drawer + scrim, with
 *      Esc / close-button / scrim-click dismissal, body scroll lock,
 *      and focus trapping.
 *
 * What this file deliberately does NOT handle (see notes at bottom):
 *   - Cart drawer open/close — owned by Dawn's <cart-drawer> (cart-drawer.js).
 *   - Sticky header — CSS (position: sticky) per chrome-notes.md.
 *
 * ---- Markup contract (for the sections built from Day 2 on) ----
 *   Drawer:   <element data-ohp-drawer="ID" aria-hidden="true"> ... </element>
 *               Section CSS sets it position:fixed, off-screen by default,
 *               z-index >= 999, and slides it in on the `.is-open` class
 *               (transition; respect prefers-reduced-motion in that CSS too).
 *   Open:     <button data-ohp-drawer-open="ID"> (e.g. the hamburger;
 *               use the ohp-icon-menu.liquid snippet for its icon)
 *   Close:    <button data-ohp-drawer-close> inside the drawer
 *               (use the ohp-icon-close.liquid snippet for its icon)
 *   The scrim (rgba(45,49,52,.4)) is created and managed here.
 *
 *   FAQ:      <details class="ohp-faq"> with a <summary class="ohp-faq__q">
 *               and an answer wrapper <div class="ohp-faq__a"> — exactly the
 *               markup ohp-faq-item.liquid emits. Add data-ohp-faq-exclusive
 *               to an ancestor to enforce one-open-at-a-time within it.
 */
(function () {
  'use strict';

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  function prefersReducedMotion() {
    return motionQuery.matches;
  }

  /* =================================================================
   * Shared: body scroll lock
   * Uses inline style (restored on unlock) so it needs no extra CSS.
   * ================================================================= */
  var scrollLocked = false;
  var previousBodyOverflow = '';
  function lockScroll() {
    if (scrollLocked) return;
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    scrollLocked = true;
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    document.body.style.overflow = previousBodyOverflow;
    scrollLocked = false;
  }

  /* =================================================================
   * Shared: lightweight focus trap (self-contained, no Dawn dependency)
   * ================================================================= */
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var trapKeydownHandler = null;

  function focusableWithin(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
      });
  }

  function trapFocus(container) {
    var focusables = focusableWithin(container);
    if (focusables.length) {
      focusables[0].focus();
    } else {
      container.setAttribute('tabindex', '-1');
      container.focus();
    }
    trapKeydownHandler = function (event) {
      if (event.key !== 'Tab') return;
      var list = focusableWithin(container);
      if (!list.length) {
        event.preventDefault();
        return;
      }
      var first = list[0];
      var last = list[list.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapKeydownHandler);
  }

  function releaseFocus() {
    if (trapKeydownHandler) {
      document.removeEventListener('keydown', trapKeydownHandler);
      trapKeydownHandler = null;
    }
  }

  /* =================================================================
   * Drawer system (powers the mobile menu; reusable for other drawers)
   * ================================================================= */
  var scrim = null;
  var activeDrawer = null;
  var lastTrigger = null;
  var scrimHideHandler = null;
  var scrimRaf = null;

  // Detach any pending "hide the scrim" transitionend callback so a reopen
  // can't be cut short by a stale handler left over from the previous close.
  function clearScrimHide() {
    if (scrimHideHandler && scrim) {
      scrim.removeEventListener('transitionend', scrimHideHandler);
    }
    scrimHideHandler = null;
  }

  function ensureScrim() {
    if (scrim) return scrim;
    scrim = document.createElement('div');
    scrim.className = 'ohp-scrim';
    scrim.setAttribute('hidden', '');
    scrim.style.position = 'fixed';
    scrim.style.top = '0';
    scrim.style.right = '0';
    scrim.style.bottom = '0';
    scrim.style.left = '0';
    scrim.style.background = 'rgba(45,49,52,.4)';
    scrim.style.opacity = '0';
    scrim.style.zIndex = '998';
    scrim.style.pointerEvents = 'none';
    scrim.style.transition = prefersReducedMotion() ? 'none' : 'opacity .25s ease';
    scrim.addEventListener('click', closeActiveDrawer);
    document.body.appendChild(scrim);
    return scrim;
  }

  function openDrawer(id, trigger) {
    var drawer = document.querySelector('[data-ohp-drawer="' + id + '"]');
    if (!drawer) return;
    if (activeDrawer) closeActiveDrawer();

    activeDrawer = drawer;
    lastTrigger = trigger || document.activeElement;

    ensureScrim();
    clearScrimHide();
    if (scrimRaf) cancelAnimationFrame(scrimRaf);
    scrim.removeAttribute('hidden');
    scrim.style.pointerEvents = 'auto';
    // next frame so the opacity transition runs
    scrimRaf = requestAnimationFrame(function () {
      scrimRaf = null;
      scrim.style.opacity = '1';
    });

    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');

    lockScroll();
    trapFocus(drawer);

    setOpenersExpanded(id, true);
  }

  // Keep every control that opens this drawer in sync for assistive tech,
  // not just the first one (multiple triggers may share a drawer id).
  function setOpenersExpanded(id, expanded) {
    var openers = document.querySelectorAll('[data-ohp-drawer-open="' + id + '"]');
    Array.prototype.forEach.call(openers, function (opener) {
      opener.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function closeActiveDrawer() {
    if (!activeDrawer) return;
    var drawer = activeDrawer;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    if (scrim) {
      // Cancel a pending scrim-open frame so it can't restore opacity after
      // close (e.g. Escape immediately after opening).
      if (scrimRaf) {
        cancelAnimationFrame(scrimRaf);
        scrimRaf = null;
      }
      var wasVisible = scrim.style.opacity === '1';
      scrim.style.opacity = '0';
      scrim.style.pointerEvents = 'none';
      clearScrimHide();
      if (prefersReducedMotion() || !wasVisible) {
        // No 1->0 opacity transition will fire (already hidden / open frame
        // never ran), so no transitionend — hide synchronously.
        scrim.setAttribute('hidden', '');
      } else {
        scrimHideHandler = function () {
          // A new drawer may have opened during the fade-out — keep the scrim.
          if (activeDrawer) return;
          scrim.setAttribute('hidden', '');
          clearScrimHide();
        };
        scrim.addEventListener('transitionend', scrimHideHandler);
      }
    }

    unlockScroll();
    releaseFocus();

    setOpenersExpanded(drawer.getAttribute('data-ohp-drawer'), false);

    if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();

    activeDrawer = null;
    lastTrigger = null;
  }

  /* =================================================================
   * FAQ accordion — progressive enhancement over native <details>
   * Without JS the <details> still opens/closes; this adds a smooth
   * height transition and optional one-open-at-a-time behavior.
   * ================================================================= */
  function answerOf(details) {
    return details.querySelector('.ohp-faq__a');
  }

  // Cancel any transition currently running on this item so a new
  // open/close can interrupt and reverse it (e.g. clicking a second
  // exclusive item mid-animation must still close the first).
  function cancelAnim(details, answer) {
    if (details._ohpRaf) {
      cancelAnimationFrame(details._ohpRaf);
      details._ohpRaf = null;
    }
    if (details._ohpAnimEnd) {
      answer.removeEventListener('transitionend', details._ohpAnimEnd);
      details._ohpAnimEnd = null;
    }
  }

  // Animate the answer's height. `opening` true grows to content height,
  // false shrinks to 0 and then closes the <details>. Starts from the
  // element's current rendered height, so it reverses cleanly mid-flight.
  // Effective open state for toggle decisions: while an animation is in
  // flight, follow its target (details.open isn't flipped until a close
  // animation finishes), so a re-click reverses instead of repeating.
  function isOpenOrOpening(details) {
    if (details._ohpAnimEnd) return details._ohpTarget === true;
    return details.open;
  }

  function animateTo(details, opening) {
    details._ohpTarget = opening;
    var answer = answerOf(details);
    if (!answer || prefersReducedMotion()) {
      details.open = opening;
      return;
    }
    cancelAnim(details, answer);
    var startHeight = answer.getBoundingClientRect().height;
    if (opening) details.open = true; // render content so it can be measured
    answer.style.overflow = 'hidden';
    answer.style.transition = 'none';
    answer.style.height = 'auto';
    var endHeight = opening ? answer.scrollHeight : 0;
    answer.style.height = startHeight + 'px';
    void answer.offsetHeight; // force reflow before transitioning

    var done = function (event) {
      if (event && event.propertyName && event.propertyName !== 'height') return;
      answer.removeEventListener('transitionend', done);
      details._ohpAnimEnd = null;
      answer.style.transition = '';
      answer.style.height = '';
      answer.style.overflow = '';
      if (!opening) details.open = false;
    };
    details._ohpAnimEnd = done;

    if (startHeight === endHeight) {
      // Nothing to animate (e.g. an opening item reversed to close before its
      // frame ran, both 0px). A no-op transition fires no transitionend, so
      // finalize synchronously to guarantee cleanup and the correct open state.
      done();
      return;
    }

    answer.addEventListener('transitionend', done);
    details._ohpRaf = requestAnimationFrame(function () {
      details._ohpRaf = null;
      answer.style.transition = 'height .25s ease';
      answer.style.height = endHeight + 'px';
    });
  }

  function animateOpen(details) {
    animateTo(details, true);
  }

  function animateClose(details) {
    animateTo(details, false);
  }

  function closeSiblings(details) {
    var group = details.closest('[data-ohp-faq-exclusive]');
    if (!group) return;
    var open = group.querySelectorAll('details.ohp-faq[open]');
    Array.prototype.forEach.call(open, function (other) {
      if (other !== details) animateClose(other);
    });
  }

  /* =================================================================
   * Event delegation
   * ================================================================= */
  document.addEventListener('click', function (event) {
    // Drawer open
    var opener = event.target.closest('[data-ohp-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.getAttribute('data-ohp-drawer-open'), opener);
      return;
    }
    // Drawer close
    if (event.target.closest('[data-ohp-drawer-close]')) {
      event.preventDefault();
      closeActiveDrawer();
      return;
    }
    // FAQ toggle (only intercept when we animate; native handles reduced-motion)
    var summary = event.target.closest('summary.ohp-faq__q');
    if (summary) {
      var details = summary.parentElement;
      if (!details || !details.classList || !details.classList.contains('ohp-faq')) return;
      if (prefersReducedMotion()) {
        // Let the browser toggle this item natively (unanimated), but still
        // enforce one-open-at-a-time for an exclusive group. details.open
        // here is the pre-toggle state, so !open means it's about to open.
        if (!details.open) closeSiblings(details);
        return;
      }
      event.preventDefault();
      if (isOpenOrOpening(details)) {
        animateClose(details);
      } else {
        closeSiblings(details);
        animateOpen(details);
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && activeDrawer) {
      closeActiveDrawer();
    }
  });

  /* =================================================================
   * Notes on Dawn overlap (no action taken here):
   *
   * Cart drawer — Dawn's <cart-drawer> custom element (cart-drawer.js)
   *   already opens on the cart icon and add-to-cart, and closes on the
   *   × button, Esc, and overlay click, with its own focus trap. We do
   *   NOT re-wire it here to avoid double handling. The OptiHealth
   *   slide-from-right timing (right .3s cubic-bezier(.4,0,.2,1)) and the
   *   overlay opacity .25s are CSS concerns, applied via the cart-drawer
   *   stylesheet when the cart section is reskinned — not JS.
   *
   * Sticky header — chrome-notes.md specifies position: sticky, top: 0,
   *   z-index: 30, which Dawn's header handles in CSS. No JS is required;
   *   no hide-on-scroll behavior was requested.
   * ================================================================= */
})();
